import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Plus, Users, FileText, AlertTriangle, LogOut, ArrowLeft, Bell, CheckCircle2, Edit2, Upload, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const EntreprisesPage = () => {
  const { user, logout } = useAuth();
  const [entreprises, setEntreprises] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntreprise, setShowAddEntreprise] = useState(false);
  const [showEditEntreprise, setShowEditEntreprise] = useState(false);
  const [showAddContrat, setShowAddContrat] = useState(false);
  const [showEditContrat, setShowEditContrat] = useState(false);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [editingEntreprise, setEditingEntreprise] = useState(null);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const fileInputRef = useRef(null);

  const [newEntreprise, setNewEntreprise] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    secteur: '',
    zone: '',
    contact_nom: '',
    contact_prenom: '',
    contact_telephone: '',
    contact_email: '',
    contact_fonction: ''
  });

  const [newContrat, setNewContrat] = useState({
    apprenant_id: '',
    entreprise_id: '',
    type_contrat: 'CDI',
    date_signature: '',
    duree_mois: '',
    salaire: '',
    horaires: '',
    commentaire: ''
  });

  const [editContrat, setEditContrat] = useState({
    type_contrat: '',
    date_signature: '',
    duree_mois: '',
    salaire: '',
    horaires: '',
    commentaire: '',
    date_fin: '',
    motif_fin: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entreprisesRes, contratsRes, alertesRes, beneficiairesRes] = await Promise.all([
        api.get('/entreprises'),
        api.get('/contrats'),
        api.get('/alertes-suivi'),
        api.get('/apprenants')
      ]);
      setEntreprises(entreprisesRes.data);
      setContrats(contratsRes.data);
      setAlertes(alertesRes.data);
      setBeneficiaires(beneficiairesRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntreprise = async () => {
    try {
      await api.post('/entreprises', newEntreprise);
      toast.success('Entreprise ajoutée avec succès');
      setShowAddEntreprise(false);
      setNewEntreprise({ nom: '', adresse: '', telephone: '', email: '', secteur: '', zone: '', contact_nom: '', contact_prenom: '', contact_telephone: '', contact_email: '', contact_fonction: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEditEntreprise = (entreprise) => {
    setEditingEntreprise({ ...entreprise });
    setShowEditEntreprise(true);
  };

  const handleUpdateEntreprise = async () => {
    try {
      await api.put(`/entreprises/${editingEntreprise.id}`, editingEntreprise);
      toast.success('Entreprise mise à jour avec succès');
      setShowEditEntreprise(false);
      setEditingEntreprise(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreateContrat = async () => {
    try {
      const contratData = {
        ...newContrat,
        salaire: parseFloat(newContrat.salaire),
        duree_mois: newContrat.duree_mois ? parseInt(newContrat.duree_mois) : null
      };
      const res = await api.post('/contrats', contratData);
      
      // Générer automatiquement les alertes de suivi à 6 mois
      await api.post(`/contrats/${res.data.id}/generer-alertes`);
      
      toast.success('Contrat créé et alertes de suivi générées');
      setShowAddContrat(false);
      setNewContrat({
        apprenant_id: '',
        entreprise_id: '',
        type_contrat: 'CDI',
        date_signature: '',
        duree_mois: '',
        salaire: '',
        horaires: '',
        commentaire: ''
      });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création du contrat');
    }
  };

  const handleEditContrat = (contrat) => {
    setSelectedContrat(contrat);
    setEditContrat({
      type_contrat: contrat.type_contrat,
      date_signature: contrat.date_signature,
      duree_mois: contrat.duree_mois || '',
      salaire: contrat.salaire || '',
      horaires: contrat.horaires || '',
      commentaire: contrat.commentaire || '',
      date_fin: contrat.date_fin || '',
      motif_fin: contrat.motif_fin || ''
    });
    setShowEditContrat(true);
  };

  const handleSaveEditContrat = async () => {
    try {
      const updateData = {
        ...editContrat,
        salaire: editContrat.salaire ? parseFloat(editContrat.salaire) : null,
        duree_mois: editContrat.duree_mois ? parseInt(editContrat.duree_mois) : null,
        date_fin: editContrat.date_fin || null,
        motif_fin: editContrat.motif_fin || null
      };
      await api.put(`/contrats/${selectedContrat.id}`, updateData);
      toast.success('Contrat modifié avec succès');
      setShowEditContrat(false);
      setSelectedContrat(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification du contrat');
    }
  };

  const handleUploadDocument = async (contratId, file) => {
    setUploadingDoc(contratId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/contrats/${contratId}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploadé avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleViewDocument = (contratId) => {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_URL}/api/contrats/${contratId}/document?token=${token}`, '_blank');
  };

  const handleMarquerAlerteEffectuee = async (alerteId) => {
    try {
      await api.put(`/alertes-suivi/${alerteId}/marquer-effectuee`, null, {
        params: { commentaires: 'Suivi effectué' }
      });
      toast.success('Alerte marquée comme effectuée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getBeneficiaireName = (id) => {
    const b = beneficiaires.find(b => b.id === id);
    return b ? `${b.prenom} ${b.nom}` : 'Inconnu';
  };

  const getEntrepriseName = (id) => {
    const e = entreprises.find(e => e.id === id);
    return e ? e.nom : 'Inconnue';
  };

  const alertesPendantes = alertes.filter(a => !a.effectuee);
  const alertesUrgentes = alertesPendantes.filter(a => {
    const datePrevue = new Date(a.date_prevue);
    const today = new Date();
    const diffDays = Math.ceil((datePrevue - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-50 py-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              data-testid="back-button"
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Entreprises & Post-Embauche
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Gestion des entreprises partenaires et suivi post-embauche
              </p>
            </div>
          </div>
          <Button
            onClick={logout}
            data-testid="logout-button"
            variant="outline"
            className="rounded-xl border-slate-200"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8">
        {/* Alertes urgentes */}
        {alertesUrgentes.length > 0 && (
          <Card className="bg-amber-50 border-amber-200 rounded-3xl p-4 mb-6 shadow-sm">
            <CardContent className="flex items-center gap-4 p-0">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div>
                <p className="font-bold text-amber-900">
                  {alertesUrgentes.length} alerte(s) de suivi urgente(s)
                </p>
                <p className="text-sm text-amber-700">
                  Des suivis post-embauche sont prévus dans les 7 prochains jours
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="entreprises" className="space-y-6">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm">
            <TabsTrigger value="entreprises" data-testid="tab-entreprises" className="rounded-lg">
              <Building2 className="w-4 h-4 mr-2" />
              Entreprises ({entreprises.length})
            </TabsTrigger>
            <TabsTrigger value="contrats" data-testid="tab-contrats" className="rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              Contrats ({contrats.length})
            </TabsTrigger>
            <TabsTrigger value="suivi" data-testid="tab-suivi" className="rounded-lg">
              <Bell className="w-4 h-4 mr-2" />
              Suivi 6 mois ({alertesPendantes.length})
            </TabsTrigger>
          </TabsList>

          {/* Onglet Entreprises */}
          <TabsContent value="entreprises">
            <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Base de données Entreprises
                </CardTitle>
                <Dialog open={showAddEntreprise} onOpenChange={setShowAddEntreprise}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-entreprise-btn" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une entreprise
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Nouvelle entreprise</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Nom de l'entreprise *</label>
                        <Input
                          data-testid="entreprise-nom"
                          value={newEntreprise.nom}
                          onChange={(e) => setNewEntreprise({ ...newEntreprise, nom: e.target.value })}
                          placeholder="Ex: SARL Dupont"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Secteur d'activité *</label>
                        <Select
                          value={newEntreprise.secteur}
                          onValueChange={(val) => setNewEntreprise({ ...newEntreprise, secteur: val })}
                        >
                          <SelectTrigger data-testid="entreprise-secteur" className="mt-1 rounded-xl">
                            <SelectValue placeholder="Sélectionner un secteur" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BTP">BTP</SelectItem>
                            <SelectItem value="Commerce">Commerce</SelectItem>
                            <SelectItem value="Restauration">Restauration</SelectItem>
                            <SelectItem value="Services">Services</SelectItem>
                            <SelectItem value="Industrie">Industrie</SelectItem>
                            <SelectItem value="Agriculture">Agriculture</SelectItem>
                            <SelectItem value="Transport">Transport</SelectItem>
                            <SelectItem value="Santé">Santé</SelectItem>
                            <SelectItem value="Autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Zone géographique *</label>
                        <Select
                          value={newEntreprise.zone}
                          onValueChange={(val) => setNewEntreprise({ ...newEntreprise, zone: val })}
                        >
                          <SelectTrigger data-testid="entreprise-zone" className="mt-1 rounded-xl">
                            <SelectValue placeholder="Sélectionner une zone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                            <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Adresse</label>
                        <Input
                          data-testid="entreprise-adresse"
                          value={newEntreprise.adresse}
                          onChange={(e) => setNewEntreprise({ ...newEntreprise, adresse: e.target.value })}
                          placeholder="Adresse complète"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Téléphone</label>
                        <Input
                          data-testid="entreprise-telephone"
                          value={newEntreprise.telephone}
                          onChange={(e) => setNewEntreprise({ ...newEntreprise, telephone: e.target.value })}
                          placeholder="0262 XX XX XX"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Email *</label>
                        <Input
                          data-testid="entreprise-email"
                          type="email"
                          value={newEntreprise.email}
                          onChange={(e) => setNewEntreprise({ ...newEntreprise, email: e.target.value })}
                          placeholder="contact@entreprise.fr"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      
                      {/* Contact en entreprise */}
                      <div className="border-t pt-4 mt-2">
                        <div className="text-sm font-bold text-slate-700 mb-3">Contact en entreprise</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium text-slate-600">Nom</label>
                            <Input
                              data-testid="contact-nom"
                              value={newEntreprise.contact_nom}
                              onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_nom: e.target.value })}
                              placeholder="Nom du contact"
                              className="mt-1 rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-600">Prénom</label>
                            <Input
                              data-testid="contact-prenom"
                              value={newEntreprise.contact_prenom}
                              onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_prenom: e.target.value })}
                              placeholder="Prénom"
                              className="mt-1 rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-600">Fonction</label>
                            <Input
                              data-testid="contact-fonction"
                              value={newEntreprise.contact_fonction}
                              onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_fonction: e.target.value })}
                              placeholder="Ex: RH, Directeur..."
                              className="mt-1 rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-600">Téléphone direct</label>
                            <Input
                              data-testid="contact-telephone"
                              value={newEntreprise.contact_telephone}
                              onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_telephone: e.target.value })}
                              placeholder="06 XX XX XX XX"
                              className="mt-1 rounded-xl"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-slate-600">Email direct</label>
                            <Input
                              data-testid="contact-email"
                              type="email"
                              value={newEntreprise.contact_email}
                              onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_email: e.target.value })}
                              placeholder="prenom.nom@entreprise.fr"
                              className="mt-1 rounded-xl"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddEntreprise(false)} className="rounded-xl">
                        Annuler
                      </Button>
                      <Button
                        data-testid="save-entreprise-btn"
                        onClick={handleCreateEntreprise}
                        disabled={!newEntreprise.nom || !newEntreprise.email || !newEntreprise.secteur}
                        className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                      >
                        Enregistrer
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {entreprises.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucune entreprise enregistrée</p>
                    <p className="text-sm text-slate-400">Commencez par ajouter une entreprise partenaire</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {entreprises.map((entreprise) => (
                      <div
                        key={entreprise.id}
                        data-testid={`entreprise-card-${entreprise.id}`}
                        className="p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-blue-200 transition-all cursor-pointer"
                        onClick={() => setSelectedEntreprise(entreprise)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-slate-900">{entreprise.nom}</h3>
                            <div className="flex gap-2 mt-1">
                              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                {entreprise.secteur}
                              </span>
                              {entreprise.zone && (
                                <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                                  {entreprise.zone}
                                </span>
                              )}
                            </div>
                          </div>
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="mt-3 space-y-1 text-sm text-slate-600">
                          <p>{entreprise.adresse}</p>
                          <p>{entreprise.telephone}</p>
                          <p>{entreprise.email}</p>
                        </div>
                        {entreprise.contact_nom && (
                          <div className="mt-2 p-2 bg-blue-50 rounded-xl">
                            <p className="text-xs font-bold text-blue-700">Contact:</p>
                            <p className="text-sm text-slate-800">
                              {entreprise.contact_prenom} {entreprise.contact_nom}
                              {entreprise.contact_fonction && <span className="text-slate-500"> ({entreprise.contact_fonction})</span>}
                            </p>
                            {entreprise.contact_telephone && (
                              <p className="text-xs text-slate-600">{entreprise.contact_telephone}</p>
                            )}
                          </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                          <p className="text-xs text-slate-500">
                            {contrats.filter(c => c.entreprise_id === entreprise.id).length} contrat(s) actif(s)
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditEntreprise(entreprise);
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Contrats */}
          <TabsContent value="contrats">
            <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Contrats de travail
                </CardTitle>
                <Dialog open={showAddContrat} onOpenChange={setShowAddContrat}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-contrat-btn" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau contrat
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Enregistrer un contrat</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Bénéficiaire *</label>
                        <Select
                          value={newContrat.apprenant_id}
                          onValueChange={(val) => setNewContrat({ ...newContrat, apprenant_id: val })}
                        >
                          <SelectTrigger data-testid="contrat-beneficiaire" className="mt-1 rounded-xl">
                            <SelectValue placeholder="Sélectionner un bénéficiaire" />
                          </SelectTrigger>
                          <SelectContent>
                            {beneficiaires.map((b) => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.prenom} {b.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Entreprise *</label>
                        <Select
                          value={newContrat.entreprise_id}
                          onValueChange={(val) => setNewContrat({ ...newContrat, entreprise_id: val })}
                        >
                          <SelectTrigger data-testid="contrat-entreprise" className="mt-1 rounded-xl">
                            <SelectValue placeholder="Sélectionner une entreprise" />
                          </SelectTrigger>
                          <SelectContent>
                            {entreprises.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Type de contrat *</label>
                        <Select
                          value={newContrat.type_contrat}
                          onValueChange={(val) => setNewContrat({ ...newContrat, type_contrat: val })}
                        >
                          <SelectTrigger data-testid="contrat-type" className="mt-1 rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CDI">CDI</SelectItem>
                            <SelectItem value="CDD">CDD</SelectItem>
                            <SelectItem value="Intérim">Intérim</SelectItem>
                            <SelectItem value="Alternance">Alternance</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newContrat.type_contrat !== 'CDI' && (
                        <div>
                          <label className="text-sm font-medium text-slate-700">Durée du contrat (mois) *</label>
                          <Input
                            data-testid="contrat-duree"
                            type="number"
                            min="1"
                            max="36"
                            value={newContrat.duree_mois}
                            onChange={(e) => setNewContrat({ ...newContrat, duree_mois: e.target.value })}
                            placeholder="Ex: 6, 12, 24..."
                            className="mt-1 rounded-xl"
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-slate-700">Date de signature *</label>
                        <Input
                          data-testid="contrat-date"
                          type="date"
                          value={newContrat.date_signature}
                          onChange={(e) => setNewContrat({ ...newContrat, date_signature: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Salaire brut mensuel (€)</label>
                        <Input
                          data-testid="contrat-salaire"
                          type="number"
                          value={newContrat.salaire}
                          onChange={(e) => setNewContrat({ ...newContrat, salaire: e.target.value })}
                          placeholder="Ex: 1800"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Horaires</label>
                        <Input
                          data-testid="contrat-horaires"
                          value={newContrat.horaires}
                          onChange={(e) => setNewContrat({ ...newContrat, horaires: e.target.value })}
                          placeholder="Ex: 35h/semaine"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Commentaire</label>
                        <Textarea
                          data-testid="contrat-commentaire"
                          value={newContrat.commentaire}
                          onChange={(e) => setNewContrat({ ...newContrat, commentaire: e.target.value })}
                          placeholder="Informations complémentaires sur le contrat..."
                          className="mt-1 rounded-xl min-h-[80px]"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddContrat(false)} className="rounded-xl">
                        Annuler
                      </Button>
                      <Button
                        data-testid="save-contrat-btn"
                        onClick={handleCreateContrat}
                        disabled={!newContrat.apprenant_id || !newContrat.entreprise_id || !newContrat.date_signature}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                      >
                        Enregistrer
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {contrats.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucun contrat enregistré</p>
                    <p className="text-sm text-slate-400">Les contrats apparaîtront ici une fois créés</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contrats.map((contrat) => (
                      <div
                        key={contrat.id}
                        data-testid={`contrat-item-${contrat.id}`}
                        className="p-4 bg-slate-50 rounded-2xl border border-slate-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                              contrat.type_contrat === 'CDI' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {contrat.type_contrat}
                              {contrat.duree_mois && ` (${contrat.duree_mois} mois)`}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">
                                {getBeneficiaireName(contrat.apprenant_id)}
                              </p>
                              <p className="text-sm text-slate-600">
                                {getEntrepriseName(contrat.entreprise_id)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-slate-900">
                                {contrat.salaire ? `${contrat.salaire.toLocaleString()}€/mois` : '-'}
                              </p>
                              <p className="text-sm text-slate-500">
                                Signé le {new Date(contrat.date_signature).toLocaleDateString('fr-FR')}
                              </p>
                              {contrat.type_contrat === 'CDI' && (
                                <span className="text-xs text-emerald-600 font-medium">Durée indéterminée</span>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditContrat(contrat)}
                              data-testid={`edit-contrat-${contrat.id}`}
                              className="rounded-xl"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {contrat.horaires && (
                          <p className="mt-2 text-sm text-slate-600">
                            Horaires: {contrat.horaires}
                          </p>
                        )}
                        {contrat.commentaire && (
                          <p className="mt-2 text-sm text-slate-500 italic">
                            💬 {contrat.commentaire}
                          </p>
                        )}
                        
                        {/* Section Document */}
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="text-sm text-slate-600">Contrat de travail</span>
                            </div>
                            <div className="flex gap-2">
                              {contrat.document_id ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDocument(contrat.id)}
                                  data-testid={`view-doc-${contrat.id}`}
                                  className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Voir le document
                                </Button>
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadDocument(contrat.id, e.target.files[0]);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
                                      input.onchange = (e) => {
                                        if (e.target.files[0]) {
                                          handleUploadDocument(contrat.id, e.target.files[0]);
                                        }
                                      };
                                      input.click();
                                    }}
                                    disabled={uploadingDoc === contrat.id}
                                    data-testid={`upload-doc-${contrat.id}`}
                                    className="rounded-xl text-amber-600 border-amber-200 hover:bg-amber-50"
                                  >
                                    <Upload className="w-4 h-4 mr-1" />
                                    {uploadingDoc === contrat.id ? 'Upload...' : 'Déposer votre contrat'}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dialog modification contrat */}
            <Dialog open={showEditContrat} onOpenChange={setShowEditContrat}>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Modifier le contrat</DialogTitle>
                  <DialogDescription>Modifiez les informations du contrat ci-dessous.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Type de contrat</label>
                    <Select
                      value={editContrat.type_contrat}
                      onValueChange={(val) => setEditContrat({ ...editContrat, type_contrat: val })}
                    >
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CDI">CDI</SelectItem>
                        <SelectItem value="CDD">CDD</SelectItem>
                        <SelectItem value="Intérim">Intérim</SelectItem>
                        <SelectItem value="Alternance">Alternance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editContrat.type_contrat !== 'CDI' && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Durée (mois)</label>
                      <Input
                        type="number"
                        min="1"
                        max="36"
                        value={editContrat.duree_mois}
                        onChange={(e) => setEditContrat({ ...editContrat, duree_mois: e.target.value })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Date de signature</label>
                    <Input
                      type="date"
                      value={editContrat.date_signature}
                      onChange={(e) => setEditContrat({ ...editContrat, date_signature: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Salaire brut mensuel (€)</label>
                    <Input
                      type="number"
                      value={editContrat.salaire}
                      onChange={(e) => setEditContrat({ ...editContrat, salaire: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Horaires</label>
                    <Input
                      value={editContrat.horaires}
                      onChange={(e) => setEditContrat({ ...editContrat, horaires: e.target.value })}
                      placeholder="Ex: 35h/semaine"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Commentaire</label>
                    <Textarea
                      value={editContrat.commentaire}
                      onChange={(e) => setEditContrat({ ...editContrat, commentaire: e.target.value })}
                      placeholder="Informations complémentaires sur le contrat..."
                      className="mt-1 rounded-xl min-h-[80px]"
                    />
                  </div>
                  
                  {/* Section Fin de contrat */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-bold text-red-700 mb-3">Fin de contrat</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Date de fin</label>
                        <Input
                          type="date"
                          value={editContrat.date_fin}
                          onChange={(e) => setEditContrat({ ...editContrat, date_fin: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Motif de fin</label>
                        <Select
                          value={editContrat.motif_fin}
                          onValueChange={(val) => setEditContrat({ ...editContrat, motif_fin: val })}
                        >
                          <SelectTrigger className="mt-1 rounded-xl">
                            <SelectValue placeholder="Sélectionner un motif" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fin_cdd">Fin de CDD</SelectItem>
                            <SelectItem value="rupture_pe_employeur">Rupture période d'essai (employeur)</SelectItem>
                            <SelectItem value="rupture_pe_beneficiaire">Rupture période d'essai (bénéficiaire)</SelectItem>
                            <SelectItem value="demission">Démission du salarié</SelectItem>
                            <SelectItem value="licenciement">Licenciement</SelectItem>
                            <SelectItem value="abandon_poste">Abandon de poste</SelectItem>
                            <SelectItem value="arret_maladie">Arrêt maladie / Maternité</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEditContrat(false)} className="rounded-xl">
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSaveEditContrat}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                  >
                    Modifier
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Onglet Suivi 6 mois */}
          <TabsContent value="suivi">
            <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Alertes de suivi post-embauche (6 mois)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alertesPendantes.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucune alerte de suivi en attente</p>
                    <p className="text-sm text-slate-400">
                      Les alertes sont générées automatiquement lors de la création d'un contrat
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alertesPendantes.sort((a, b) => new Date(a.date_prevue) - new Date(b.date_prevue)).map((alerte) => {
                      const datePrevue = new Date(alerte.date_prevue);
                      const today = new Date();
                      const diffDays = Math.ceil((datePrevue - today) / (1000 * 60 * 60 * 24));
                      const isUrgent = diffDays <= 7 && diffDays >= 0;
                      const isPassed = diffDays < 0;

                      return (
                        <div
                          key={alerte.id}
                          data-testid={`alerte-item-${alerte.id}`}
                          className={`p-4 rounded-2xl border ${
                            isPassed 
                              ? 'bg-red-50 border-red-200' 
                              : isUrgent 
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isPassed 
                                  ? 'bg-red-100 text-red-600' 
                                  : isUrgent 
                                    ? 'bg-amber-100 text-amber-600'
                                    : 'bg-blue-100 text-blue-600'
                              }`}>
                                <Bell className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-bold text-slate-900">
                                  {getBeneficiaireName(alerte.apprenant_id)}
                                </p>
                                <p className="text-sm text-slate-600">
                                  Suivi mois {alerte.mois_suivi} • {getEntrepriseName(
                                    contrats.find(c => c.id === alerte.contrat_id)?.entreprise_id
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className={`font-bold ${
                                  isPassed ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-900'
                                }`}>
                                  {datePrevue.toLocaleDateString('fr-FR')}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {isPassed 
                                    ? `En retard de ${Math.abs(diffDays)} jour(s)` 
                                    : diffDays === 0 
                                      ? "Aujourd'hui"
                                      : `Dans ${diffDays} jour(s)`
                                  }
                                </p>
                              </div>
                              <Button
                                data-testid={`marquer-effectuee-${alerte.id}`}
                                onClick={() => handleMarquerAlerteEffectuee(alerte.id)}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Effectué
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Historique des suivis effectués */}
                {alertes.filter(a => a.effectuee).length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-bold text-slate-700 mb-4">Suivis effectués</h3>
                    <div className="space-y-2">
                      {alertes.filter(a => a.effectuee).map((alerte) => (
                        <div
                          key={alerte.id}
                          className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            <span className="text-slate-700">
                              {getBeneficiaireName(alerte.apprenant_id)} - Mois {alerte.mois_suivi}
                            </span>
                          </div>
                          <span className="text-sm text-slate-500">
                            Prévu le {new Date(alerte.date_prevue).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog Modification Entreprise */}
      <Dialog open={showEditEntreprise} onOpenChange={setShowEditEntreprise}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l'entreprise</DialogTitle>
          </DialogHeader>
          {editingEntreprise && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Nom de l'entreprise *</label>
                <Input
                  value={editingEntreprise.nom}
                  onChange={(e) => setEditingEntreprise({ ...editingEntreprise, nom: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Secteur d'activité *</label>
                <Select
                  value={editingEntreprise.secteur}
                  onValueChange={(val) => setEditingEntreprise({ ...editingEntreprise, secteur: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BTP">BTP</SelectItem>
                    <SelectItem value="Commerce">Commerce</SelectItem>
                    <SelectItem value="Restauration">Restauration</SelectItem>
                    <SelectItem value="Services">Services</SelectItem>
                    <SelectItem value="Industrie">Industrie</SelectItem>
                    <SelectItem value="Agriculture">Agriculture</SelectItem>
                    <SelectItem value="Transport">Transport</SelectItem>
                    <SelectItem value="Santé">Santé</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Zone géographique</label>
                <Select
                  value={editingEntreprise.zone || ''}
                  onValueChange={(val) => setEditingEntreprise({ ...editingEntreprise, zone: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner une zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                    <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Adresse</label>
                <Input
                  value={editingEntreprise.adresse || ''}
                  onChange={(e) => setEditingEntreprise({ ...editingEntreprise, adresse: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Téléphone</label>
                <Input
                  value={editingEntreprise.telephone || ''}
                  onChange={(e) => setEditingEntreprise({ ...editingEntreprise, telephone: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email *</label>
                <Input
                  type="email"
                  value={editingEntreprise.email}
                  onChange={(e) => setEditingEntreprise({ ...editingEntreprise, email: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              
              {/* Contact en entreprise */}
              <div className="border-t pt-4 mt-2">
                <div className="text-sm font-bold text-slate-700 mb-3">Contact en entreprise</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Nom</label>
                    <Input
                      value={editingEntreprise.contact_nom || ''}
                      onChange={(e) => setEditingEntreprise({ ...editingEntreprise, contact_nom: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Prénom</label>
                    <Input
                      value={editingEntreprise.contact_prenom || ''}
                      onChange={(e) => setEditingEntreprise({ ...editingEntreprise, contact_prenom: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Fonction</label>
                    <Input
                      value={editingEntreprise.contact_fonction || ''}
                      onChange={(e) => setEditingEntreprise({ ...editingEntreprise, contact_fonction: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Téléphone direct</label>
                    <Input
                      value={editingEntreprise.contact_telephone || ''}
                      onChange={(e) => setEditingEntreprise({ ...editingEntreprise, contact_telephone: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-600">Email direct</label>
                    <Input
                      type="email"
                      value={editingEntreprise.contact_email || ''}
                      onChange={(e) => setEditingEntreprise({ ...editingEntreprise, contact_email: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEntreprise(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button
              onClick={handleUpdateEntreprise}
              disabled={!editingEntreprise?.nom || !editingEntreprise?.email || !editingEntreprise?.secteur}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntreprisesPage;
